/**
 * `/login/verify`: paste the code from the email (or follow the link, which lands here with `challengeId` and
 * `code` in the query and auto-submits). The API answers with a session or the next factor.
 */
import { Button, Field, Input } from '@windrop/ui';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useSession } from '../../auth/session';
import { identityApi, type VerifyLoginResponse } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { AuthLayout } from './AuthLayout';
import type { LoginFlowState } from './Login';

export function useLoginFlow(): LoginFlowState | null {
  const location = useLocation();
  const state = location.state as Partial<LoginFlowState> | null;
  const params = new URLSearchParams(location.search);
  // The API's sign-in e-mail (and the operator CLI) link to `?challenge=<id>&code=<code>`; `challengeId` is kept.
  const challengeId = state?.challengeId ?? params.get('challengeId') ?? params.get('challenge') ?? undefined;
  if (!challengeId) return null;
  return {
    challengeId,
    method: state?.method ?? 'MAGIC_LINK',
    email: state?.email ?? params.get('email') ?? '',
    expiresAt: state?.expiresAt ?? '',
    code: state?.code ?? params.get('code') ?? undefined,
    next: state?.next ?? params.get('next') ?? '/',
  };
}

/** Routes a verify response: session → home, else the next factor screen (or authenticator enrolment). */
export function useContinueLogin() {
  const navigate = useNavigate();
  const { setSession } = useSession();
  return (res: VerifyLoginResponse, flow: LoginFlowState, code: string) => {
    if (res.next === 'DONE') {
      setSession(res.session);
      navigate(flow.next ?? '/', { replace: true });
      return;
    }
    const state: LoginFlowState = { ...flow, challengeId: res.challengeId, code };
    const target =
      res.next === 'TOTP' ? '/login/totp' : res.next === 'ENROLL' ? '/login/enroll' : '/login/passkey';
    navigate(target, { state, replace: true });
  };
}

export function VerifyPage() {
  const flow = useLoginFlow();
  const continueLogin = useContinueLogin();
  const [code, setCode] = useState(flow?.code ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const autoSubmitted = useRef(false);

  const submit = async (value: string) => {
    if (!flow) return;
    if (value.trim().length < 6) {
      setError('Enter the full code from the email (at least 6 characters).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      continueLogin(
        await identityApi.verify({ challengeId: flow.challengeId, code: value.trim() }),
        flow,
        value.trim(),
      );
    } catch (err) {
      setError(
        isApiError(err) ? err.message : 'That code was not accepted. Request a new link if it has expired.',
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (flow?.code && !autoSubmitted.current) {
      autoSubmitted.current = true;
      void submit(flow.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!flow) {
    return (
      <AuthLayout title="Sign-in link expired">
        <p>Start again to receive a fresh link.</p>
        <Button href="/login">Back to sign in</Button>
      </AuthLayout>
    );
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit(code);
  };

  return (
    <AuthLayout title="Check your email">
      <p>
        We sent a sign-in link to <strong>{flow.email || 'your address'}</strong>. Open it on this device or
        paste the code below.
      </p>
      {flow.method === 'DEV_CODE' ? (
        <div className="wdc-callout" data-tone="warning" data-testid="dev-login-hint">
          <p>
            <strong>Development login is enabled.</strong> Use the seed code <code>000000</code>{' '}
            (DEV_LOGIN_ENABLED; never on in production).
          </p>
        </div>
      ) : null}
      <form onSubmit={onSubmit} className="wd-stack" noValidate>
        <Field label="Sign-in code" required>
          <Input
            preset="otp"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            data-testid="login-code"
          />
        </Field>
        {error ? (
          <p role="alert" className="text-error">
            {error}
          </p>
        ) : null}
        <Button type="submit" loading={busy} loadingLabel="Verifying" block>
          Verify code
        </Button>
        <p className="wd-muted">
          Wrong address? <Link to="/login">Start again</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
